import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Upload, Instagram, Loader } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { importUrl } from './importSlice';
import { uploadFiles } from '../upload/uploadSlice';
import ALLOWED_TYPES from '../upload/AllowedTypes';
import useLimits from '../plan/hooks/limits';
import { RootState } from '../../reducers';
import store from '../../store';
import { useBooleanFeature } from '../../hooks/useFeatures';
import { initializeRecorder } from '../recording/recordingSlice';
import { GlobalDialogProps } from '../../types';

const ImportModal: React.FC<GlobalDialogProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { teamId, folderId } = useParams<{
    teamId: string;
    folderId: string;
  }>();
  const initialUrl = useSelector(
    (state: RootState) => state.globalModals.actionData.url || '',
  );
  const { library } = useSelector((state: RootState) => state);
  const [url, setUrl] = useState(initialUrl);
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const guardLimits = useLimits();
  const { getFeature } = useBooleanFeature();
  const isUploadUnguarded = getFeature('feature_unguard_upload');
  const isImportUrlUnguarded = getFeature('feature_unguard_import_url');
  const [importing, setImporting] = useState(false);
  const [showInstagramNotice, setShowInstagramNotice] = useState(false);

  // Helper function to check if URL is Instagram
  const isInstagramUrl = (urlToCheck: string): boolean => {
    const lowercaseUrl = urlToCheck.toLowerCase();
    return (
      lowercaseUrl.includes('instagram.com/p/') ||
      lowercaseUrl.includes('instagram.com/reel/') ||
      lowercaseUrl.includes('instagram.com/tv/')
    );
  };

  // Check for Instagram URL when URL changes
  useEffect(() => {
    const trimmedUrl = url.trim();
    if (trimmedUrl && isInstagramUrl(trimmedUrl)) {
      setShowInstagramNotice(true);
    } else {
      setShowInstagramNotice(false);
    }
  }, [url]);

  // Conditional guard function that respects the feature flag for upload-related actions
  const conditionalUploadGuard = React.useCallback(
    (limitType: string, callback: () => void) => {
      if (isUploadUnguarded && limitType === 'importModalUploadButton') {
        // If upload is unguarded and this is an upload action, execute callback directly
        callback();
      } else {
        // Use normal guard limits
        guardLimits(limitType as any, callback);
      }
    },
    [isUploadUnguarded, guardLimits],
  );

  // Conditional guard function that respects the feature flag for import URL actions
  const conditionalImportGuard = React.useCallback(
    (limitType: string, callback: () => void) => {
      if (isImportUrlUnguarded && limitType === 'importUrlButton') {
        // If import URL is unguarded and this is an import URL action, execute callback directly
        callback();
      } else {
        // Use normal guard limits
        guardLimits(limitType as any, callback);
      }
    },
    [isImportUrlUnguarded, guardLimits],
  );

  useEffect(() => {
    if (initialUrl) {
      setUrl(initialUrl);
    }
  }, [initialUrl]);

  const handleImport = async () => {
    const trimmedUrl = url.trim();
    if (!trimmedUrl) {
      toast.error(t('import.modal.url.error'));
      return;
    }

    if (!teamId || !folderId) {
      toast.error(t('import.modal.url.error'));
      return;
    }

    // Check if it's an Instagram URL
    if (isInstagramUrl(trimmedUrl)) {
      // Open Instagram URL in new tab
      window.open(trimmedUrl, '_blank');

      // Initialize screen recorder with browser audio only, no microphone, no video
      const settings = {
        audioSettings: { microphoneAudio: false, browserAudio: true },
        videoSettings: { screen: true, webCam: false },
      };

      dispatch(initializeRecorder({ teamId, folderId, settings }));

      // Show toast with instructions
      toast(t('import.modal.instagram.description'));

      // Close the modal
      onClose();
      return;
    }

    setImporting(true);
    conditionalImportGuard('importUrlButton', async () => {
      try {
        // Dispatch importUrl and wait for the result
        await dispatch(importUrl({ teamId, folderId, url: trimmedUrl }));

        // Get initial timestamp to detect refresh
        const initialRefreshTimestamp = library.lastRefreshCompleted;

        // Check if the grid has refreshed by comparing timestamps
        const waitForGridRefresh = () => {
          return new Promise<void>((resolve) => {
            // Maximum time to wait (3 seconds)
            const maxWaitTime = 4000;
            const startTime = Date.now();

            const checkRefresh = () => {
              const currentState = store.getState();
              const currentTimestamp =
                currentState.library.lastRefreshCompleted;
              const importStatus = currentState.import.status;

              // If there's a failure, resolve immediately
              if (importStatus === 'failed') {
                resolve();
                return;
              }

              // If the timestamp has changed or max time reached, resolve
              if (
                currentTimestamp > initialRefreshTimestamp ||
                Date.now() - startTime > maxWaitTime
              ) {
                resolve();
              } else {
                // Check again in 100ms
                setTimeout(checkRefresh, 100);
              }
            };

            // Start checking
            checkRefresh();
          });
        };

        // Wait for the grid to refresh before closing
        await waitForGridRefresh();

        setImporting(false);
        // Now close the modal
        onClose();
      } catch (importError) {
        // If there's an error, close the modal immediately as this indicates an API failure
        console.error('Import error:', importError);
        setImporting(false);
        onClose();
      }
    });
  };

  const handleUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUrl(event.target.value);
    // Clear errors when user starts typing again
    if (error) {
      setError('');
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !importing && url.trim()) {
      event.preventDefault();
      handleImport();
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    const { files } = event.dataTransfer;
    if (teamId && folderId && files) {
      const filesArray = Array.from(files as ArrayLike<File>);
      const filteredFiles = filesArray.filter((file) =>
        ALLOWED_TYPES.includes(file.type),
      );
      dispatch(uploadFiles({ teamId, folderId, files: filteredFiles }));
      onClose();
    }
  };

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (teamId && folderId && event.target.files) {
      const filesArray = Array.from(event.target.files as ArrayLike<File>);
      const filteredFiles = filesArray.filter((file) =>
        ALLOWED_TYPES.includes(file.type),
      );
      dispatch(uploadFiles({ teamId, folderId, files: filteredFiles }));
      onClose();
    }
  };

  const handleUploadClick = () => {
    conditionalUploadGuard('importModalUploadButton', () => {
      document.getElementById('file-input')?.click();
    });
  };

  const handleDirectImport = async () => {
    const trimmedUrl = url.trim();
    if (!trimmedUrl || !teamId || !folderId) {
      toast.error(t('import.modal.url.error'));
      return;
    }

    setImporting(true);
    conditionalImportGuard('importUrlButton', async () => {
      try {
        // Dispatch importUrl and wait for the result
        await dispatch(importUrl({ teamId, folderId, url: trimmedUrl }));

        // Get initial timestamp to detect refresh
        const initialRefreshTimestamp = library.lastRefreshCompleted;

        // Check if the grid has refreshed by comparing timestamps
        const waitForGridRefresh = () => {
          return new Promise<void>((resolve) => {
            // Maximum time to wait (3 seconds)
            const maxWaitTime = 4000;
            const startTime = Date.now();

            const checkRefresh = () => {
              const currentState = store.getState();
              const currentTimestamp =
                currentState.library.lastRefreshCompleted;
              const importStatus = currentState.import.status;

              // If there's a failure, resolve immediately
              if (importStatus === 'failed') {
                resolve();
                return;
              }

              // If the timestamp has changed or max time reached, resolve
              if (
                currentTimestamp > initialRefreshTimestamp ||
                Date.now() - startTime > maxWaitTime
              ) {
                resolve();
              } else {
                // Check again in 100ms
                setTimeout(checkRefresh, 100);
              }
            };

            // Start checking
            checkRefresh();
          });
        };

        // Wait for the grid to refresh before closing
        await waitForGridRefresh();

        setImporting(false);
        // Now close the modal
        onClose();
      } catch (importError) {
        // If there's an error, close the modal immediately as this indicates an API failure
        console.error('Import error:', importError);
        setImporting(false);
        onClose();
      }
    });
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('import.modal.title')}</DialogTitle>
          <DialogDescription>
            {t('import.modal.dialogDescription')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Input
              type="url"
              value={url}
              onChange={handleUrlChange}
              onKeyPress={handleKeyPress}
              placeholder={t('import.modal.url.placeholder')}
              className="w-full"
            />
            <p className="text-xs text-muted-foreground">
              {t('import.modal.url.note')}
            </p>
          </div>

          {showInstagramNotice && (
            <div className="rounded-lg border bg-card p-4">
              <div className="flex gap-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-[#f09433] via-[#dc2743] to-[#bc1888]">
                  <Instagram className="h-4 w-4 text-white" />
                </div>
                <div className="flex-1 space-y-1">
                  <p className="text-sm font-medium leading-none">
                    {t('import.modal.instagram.title')}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t('import.modal.instagram.description')}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Button
              onClick={handleImport}
              disabled={!teamId || !folderId || importing}
              className="w-full"
            >
              {importing ? (
                <>
                  <Loader className="mr-2 h-4 w-4 animate-spin" />
                  {t('import.modal.importing')}
                </>
              ) : (
                <>
                  {showInstagramNotice
                    ? t('import.modal.instagram.button')
                    : t('actions.menu.importUrl')}
                </>
              )}
            </Button>

            {showInstagramNotice && (
              <div className="flex flex-col items-center space-y-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowInstagramNotice(false);
                    handleDirectImport();
                  }}
                  className="h-auto py-1 text-xs font-normal underline-offset-4 hover:underline"
                >
                  {t('import.modal.instagram.directImport')}
                </Button>
                <p className="text-xs text-muted-foreground">
                  {t('import.modal.instagram.directImportNote')}
                </p>
              </div>
            )}
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                {t('import.modal.upload.or')}
              </span>
            </div>
          </div>

          <div
            role="button"
            tabIndex={0}
            className={cn(
              'flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 transition-colors',
              isDragging
                ? 'border-primary bg-primary/5'
                : 'border-muted-foreground/25 hover:border-primary hover:bg-accent',
            )}
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onClick={handleUploadClick}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleUploadClick();
              }
            }}
          >
            <Upload
              className={cn(
                'mb-3 h-10 w-10',
                isDragging ? 'text-primary' : 'text-muted-foreground',
              )}
            />
            <p className="mb-2 text-sm font-medium">
              {isDragging
                ? t('import.modal.upload.dragDrop.dragging')
                : t('import.modal.upload.dragDrop.idle')}
            </p>
            <Button
              variant="secondary"
              size="sm"
              data-testid="upload-file-button-upload-modal"
              onClick={(e) => {
                e.stopPropagation();
                handleUploadClick();
              }}
            >
              {t('import.modal.upload.button')}
            </Button>
          </div>
          <input
            id="file-input"
            data-testid="file-input"
            type="file"
            multiple
            accept={ALLOWED_TYPES.join(',')}
            className="hidden"
            onChange={handleFileChange}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImportModal;
